import { PreviewConfigBase, PreviewState } from 'src/configs/previewConfigurations/types';
import { PreviewConstants } from 'src/constants';
import { PreviewAssets } from 'src/types';

import { createCanvas } from './canvasUtils';
import { previewRenderer } from './renderers/previewRenderer';

/**
 * Generate preview image for a specific device state/variant
 *
 * Configuration-driven preview generation:
 * 1. Gets state/variant config from config.stateConfigs
 * 2. Creates canvas with correct dimensions
 * 3. Renders layers using unified previewRenderer
 * 4. Returns data URL of rendered preview
 *
 * @param assets - Asset URLs (background, logo, etc.)
 * @param state - State/variant key (e.g., "Selected", "10Inch_Landscape_FOS")
 * @param config - Preview configuration containing stateConfigs
 * @returns Data URL of rendered preview image
 *
 * @example FireTV Selected State
 * const url = await generatePreview(
 *   { BACKGROUND_IMAGE: 'bg.jpg', LOGO: 'logo.png' },
 *   'Selected',
 *   fireTVConfig
 * );
 *
 * @example FireTablet 10" Landscape
 * const url = await generatePreview(
 *   { BACKGROUND_IMAGE: 'bg.jpg', LOGO: 'logo.png' },
 *   '10Inch_Landscape_FOS',
 *   fireTabletsConfig
 * );
 */
export const generatePreview = async (
  assets: PreviewAssets,
  state: PreviewState,
  config: PreviewConfigBase,
): Promise<string> => {
  const stateConfig = config.stateConfigs[state];

  if (!stateConfig) {
    throw new Error(`No config found for state: ${state}`);
  }

  const canvas = createCanvas(stateConfig.dimensions.width, stateConfig.dimensions.height);
  const ctx = canvas.getContext(PreviewConstants.CANVAS.CONTEXT);
  if (!ctx) throw new Error('Failed to get canvas context');

  await previewRenderer(ctx, assets, stateConfig);

  return canvas.toDataURL(
    PreviewConstants.CANVAS.IMAGE_FORMAT,
    PreviewConstants.CANVAS.IMAGE_QUALITY,
  );
};
